import Hammer from 'hammerjs';
import {trimValue} from '../utilities/utilities';
import ScrollAnimation from './scroll-animation';

const settings = {
    classNames: {
        dragging: 'image-comparison--dragging',
    },

    selectors: {
        containers: '.image-comparison',
        handlePositionElement: '.image-comparison__handle',
        handleDragElement: '.image-comparison__handle > button',
        rightImageContainer: '.image-comparison__right',
    },

    animation: {
        attractTriggerProgressTop: 0.8,
        attractTriggerProgressBottom: 0.2,
        attractMagnitude: 10,
    },
};

export default class ImageComparison extends ScrollAnimation {
    constructor(container) {
        super(container);

        /** @type {boolean} */
        this.attractPlayed = false;

        /** @type {Element} */
        this.container = container;

        // Find DOM elements
        /** @type {Element} */
        this.handlePositionElement = this.container
            .querySelector(settings.selectors.handlePositionElement);
        if (!this.handlePositionElement) {
            return;
        }

        /** @type {Element} */
        this.handleDragElement = this.container
            .querySelector(settings.selectors.handleDragElement);
        if (!this.handleDragElement) {
            return;
        }

        /** @type {Element} */
        this.rightImageContainer = this.container
            .querySelector(settings.selectors.rightImageContainer);
        if (!this.rightImageContainer) {
            return;
        }

        // Add drag
        const mc = new Hammer.Manager(this.handleDragElement);

        mc.add(
            new Hammer.Pan({
                direction: Hammer.DIRECTION_ALL,
            }),
        );

        let startPosition;
        mc.on('panstart', (event) => {
            // Stop any ongoing animation
            if (this.attractTimeline) {
                this.attractTimeline.kill();
                this.attractTimeline = null;
            }

            // Don't play the animation if the user has already
            // fiddled with the handle
            if (!this.attractPlayed) {
                this.attractPlayed = true;
            }

            // Record our starting position
            startPosition = this.handlePositionElement.offsetLeft;

            // Add our "dragging" class
            this.container
                .classList
                .add(settings.classNames.dragging);
        });

        mc.on('panend', () => {
            // Remove our "dragging" class
            this.container
                .classList
                .remove(settings.classNames.dragging);
        });

        mc.on('panmove', (event) => {
            let newPosition = startPosition + event.deltaX;
            if (newPosition < 0) {
                newPosition = 0;
            } else if (newPosition > this.container.clientWidth) {
                newPosition = this.container.clientWidth;
            }

            newPosition = trimValue(
                (newPosition / this.container.clientWidth) * 100,
            );

            this.handlePositionElement.style.left = `${newPosition}%`;
            this.rightImageContainer.style.width = `${100 - newPosition}%`;
        });

        // Add keyboard navigation
        this.handleDragElement.addEventListener(
            'keydown',
            (event) => {
                switch (event.key) {
                    case 'ArrowLeft':
                        {
                            // Stop any ongoing animation
                            if (this.attractTimeline) {
                                this.attractTimeline.progress(1);
                                this.attractTimeline = null;
                            }

                            // Determine current position as a percentage
                            let position = (
                                this.handlePositionElement.offsetLeft /
                                this.container.clientWidth
                            );

                            // Shift either to the 50% or 0% position
                            if (position > 0.5) {
                                position = 50;
                            } else {
                                position = 0;
                            }

                            this.handlePositionElement.style.left = `${position}%`;
                            this.rightImageContainer.style.width = `${100 - position}%`;
                        }
                        break;

                    case 'ArrowRight':
                        {
                            // Stop any ongoing animation
                            if (this.attractTimeline) {
                                this.attractTimeline.progress(1);
                                this.attractTimeline = null;
                            }

                            // Determine current position as a percentage
                            let position = (
                                this.handlePositionElement.offsetLeft /
                                this.container.clientWidth
                            );

                            // Shift either to the 50% or 100% position
                            if (position < 0.5) {
                                position = 50;
                            } else {
                                position = 100;
                            }

                            this.handlePositionElement.style.left = `${position}%`;
                            this.rightImageContainer.style.width = `${100 - position}%`;
                        }
                        break;

                    default:
                        break;
                }
            },
        );
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .querySelectorAll(settings.selectors.containers);

        for (let i=0; i<elements.length; i++) {
            new ImageComparison(elements[i]);
        }
    }

    // From ScrollAnimation
    setAnimationProgress(
        overallProgress,
        topProgress,
        centerProgress,
    ) {
        // Call base class
        super.setAnimationProgress(
            overallProgress,
            topProgress,
            centerProgress,
        );

        // Stop here if we've already played our attract animation
        if (this.attractPlayed) {
            return;
        }

        if (
            centerProgress >= settings.animation.attractTriggerProgressBottom &&
            centerProgress < settings.animation.attractTriggerProgressTop
        ) {
            // Trigger the animation
            this.attractPlayed = true;
            this.triggerAttractAnimation();
        }
    }

    triggerAttractAnimation() {
        // Stop any ongoing animation
        if (this.attractTimeline) {
            this.attractTimeline.progress(1);
            this.attractTimeline = null;
        }

        this.attractTimeline = gsap.timeline();

        // To the right
        this.attractTimeline.to(
            this.handlePositionElement,
            0.5,
            {
                ease: 'power1.inOut',
                left: `${50 + settings.animation.attractMagnitude}%`,
            },
            0,
        );

        this.attractTimeline.to(
            this.rightImageContainer,
            0.5,
            {
                ease: 'power1.inOut',
                width: `${50 - settings.animation.attractMagnitude}%`,
            },
            0,
        );

        // To the left
        this.attractTimeline.add('left');
        this.attractTimeline.to(
            this.handlePositionElement,
            0.5,
            {
                ease: 'power1.inOut',
                left: `${50 - settings.animation.attractMagnitude}%`,
            },
            'left',
        );

        this.attractTimeline.to(
            this.rightImageContainer,
            0.5,
            {
                ease: 'power1.inOut',
                width: `${50 + settings.animation.attractMagnitude}%`,
            },
            'left',
        );

        // Back to center
        this.attractTimeline.add('center');
        this.attractTimeline.to(
            this.handlePositionElement,
            0.5,
            {
                ease: 'power1.inOut',
                left: '50%',
            },
            'center',
        );

        this.attractTimeline.to(
            this.rightImageContainer,
            0.5,
            {
                ease: 'power1.inOut',
                width: '50%',
            },
            'center',
        );

        this.attractTimeline.add(() => {
            this.attractTimeline = null;
        });
    }
}
